import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconBolt,
  IconBrandInstagram,
  IconLayoutGrid,
  IconMoodSmile,
  IconPaperclip,
  IconPhoto,
  IconUpload,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconTemplate,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import { IgMessageContent } from '../../components/inbox/registries/instagram-content';
import { CrmPanel, CrmPanelEmpty } from '../../components/inbox/CrmPanel';
import { MediaLibraryPicker } from '../../components/inbox/MediaLibraryPicker';
import { usePanelCollapse } from '../../components/inbox/use-panel-collapse';
import { CollapseRail } from '../../components/CollapseRail';
import type { PreviewPayload } from '../../components/inbox/template-preview';
import { motion } from 'framer-motion';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { QuickRepliesPopover } from '../../components/QuickRepliesPopover';
import { useInstagramRealtime } from '../../lib/realtime';
import { THREAD_PAGE_LIMIT, flattenThread } from '../../lib/thread-cache';
import { useThreadScroll } from '../../components/inbox/useThreadScroll';
import {
  DateDivider,
  ReadReceipt,
  formatTime,
  groupMessages,
  messagingWindowState,
  timeAgo,
} from '../../lib/chat-format';

// emoji-picker-react ships ~200KB gzipped of unicode data — keep it lazy.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Instagram only supports a fixed reaction set; map each to a display glyph.
const IG_REACTIONS: { emoji: string; reaction: string }[] = [
  { emoji: '❤️', reaction: 'love' },
  { emoji: '👍', reaction: 'like' },
  { emoji: '😂', reaction: 'laugh' },
  { emoji: '😮', reaction: 'wow' },
  { emoji: '😢', reaction: 'sad' },
  { emoji: '😠', reaction: 'angry' },
];

/** Display fields the inbox needs from a conversation row, plus the Contact
 *  fallbacks the API now joins in. Kept structural so the tRPC-inferred row
 *  satisfies it without an explicit cast. */
interface IgConvoDisplay {
  igsid: string;
  profileName?: string | null;
  username?: string | null;
  profilePictureUrl?: string | null;
  contactName?: string | null;
  contactUsername?: string | null;
  contactPicture?: string | null;
}

/** Fallback chain: IG profile name → linked Contact name → @username (IG, then
 *  Contact) → raw IGSID as a last resort. */
function igDisplayName(c: IgConvoDisplay): string {
  if (c.profileName) return c.profileName;
  if (c.contactName) return c.contactName;
  if (c.username) return `@${c.username}`;
  if (c.contactUsername) return `@${c.contactUsername}`;
  return c.igsid;
}

function igAvatarSrc(c: IgConvoDisplay): string | undefined {
  return c.profilePictureUrl ?? c.contactPicture ?? undefined;
}

/** Secondary line under the name — the @handle when we have one, else the IGSID. */
function igSubtitle(c: IgConvoDisplay): string {
  if (c.username) return `@${c.username}`;
  if (c.contactUsername) return `@${c.contactUsername}`;
  return c.igsid;
}

type IgConvo = IgConvoDisplay & {
  id: number;
  instagramAccountId: number;
  unreadCount: number;
  lastMessage?: string | null;
  lastMessageAt?: string | Date | null;
  lastIncomingAt?: string | Date | null;
  contactUid?: string | null;
  contactId?: number | null;
};

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'unread', label: 'Unread first' },
  { value: 'alpha', label: 'Name (A–Z)' },
];

function InstagramInbox() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [sortBy, setSortBy] = useState<'recent' | 'unread' | 'alpha'>('recent');
  useInstagramRealtime(activeId);

  const conversations = trpc.instagram.inbox.conversations.useQuery(
    {
      page: 1,
      pageSize: 100,
      sortBy,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    },
    { refetchInterval: 30_000, refetchIntervalInBackground: true },
  );

  const items = useMemo(() => conversations.data?.items ?? [], [conversations.data]);
  const activeConvo = items.find((c) => c.id === activeId);
  // Deep-link: auto-open the conversation for ?contactId (from a contact's
  // "Open Instagram" button) once it appears in the loaded list.
  const deepLink = Route.useSearch();
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  useEffect(() => {
    if (deepLinkApplied || !deepLink.contactId || activeId != null) return;
    const match = items.find((c) => c.contactId === deepLink.contactId);
    if (match) {
      setActiveId(match.id);
      setDeepLinkApplied(true);
    }
  }, [deepLink.contactId, items, activeId, deepLinkApplied]);
  const { leftCollapsed, rightCollapsed, toggleLeft, toggleRight } = usePanelCollapse();
  const leftWidth = activeId != null && leftCollapsed ? '0px' : '340px';
  const rightWidth = activeId != null && !rightCollapsed ? '320px' : '0px';

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `${leftWidth} minmax(0, 1fr) ${rightWidth}`,
        gridTemplateRows: 'minmax(0, 1fr)',
        gap: 8,
        height: 'calc(100vh - 100px)',
        overflow: 'hidden',
        transition: 'grid-template-columns 220ms ease',
      }}
    >
      <Card
        p={0}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Stack p="sm" gap="xs">
          <Title order={4}>Instagram</Title>
          <TextInput
            size="xs"
            placeholder="Search name, @handle or ID"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Select
            size="xs"
            data={SORT_OPTIONS}
            value={sortBy}
            onChange={(v) => setSortBy((v as 'recent' | 'unread' | 'alpha') ?? 'recent')}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        </Stack>
        <Divider />
        <ScrollArea style={{ flex: 1 }}>
          {conversations.isPending ? (
            <Stack p="sm">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} height={56} radius="sm" />
              ))}
            </Stack>
          ) : (
            <Stack gap={0}>
              {items.map((c) => (
                <motion.button
                  key={c.id}
                  whileHover={{ backgroundColor: 'var(--mantine-color-gray-1)' }}
                  style={{
                    background:
                      c.id === activeId ? 'var(--mantine-color-blue-light)' : 'transparent',
                    border: 'none',
                    padding: 12,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onClick={() => setActiveId(c.id)}
                >
                  <Group wrap="nowrap" gap="sm">
                    <Avatar size="md" radius="xl" src={igAvatarSrc(c)}>
                      <IconBrandInstagram size={14} />
                    </Avatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={600} truncate>
                          {igDisplayName(c)}
                        </Text>
                        {c.lastMessageAt && (
                          <Text size="xs" c="dimmed">
                            {timeAgo(new Date(c.lastMessageAt))}
                          </Text>
                        )}
                      </Group>
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="xs" c="dimmed" truncate>
                          {c.lastMessage ?? igSubtitle(c)}
                        </Text>
                        {c.unreadCount > 0 && (
                          <Badge size="xs" color="blue">
                            {c.unreadCount}
                          </Badge>
                        )}
                      </Group>
                    </Box>
                  </Group>
                </motion.button>
              ))}
              {items.length === 0 && (
                <Text c="dimmed" ta="center" p="md">
                  {debouncedSearch.trim() ? 'No matches.' : 'No conversations yet.'}
                </Text>
              )}
            </Stack>
          )}
        </ScrollArea>
      </Card>

      <Box
        className="collapse-pane"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {activeId ? (
          <Conversation conversationId={activeId} convo={activeConvo} />
        ) : (
          <Center style={{ flex: 1 }}>
            <Stack align="center" gap="xs">
              <IconBrandInstagram size={48} stroke={1.2} />
              <Text c="dimmed">Select an Instagram conversation</Text>
            </Stack>
          </Center>
        )}
        {activeId && (
          <>
            <CollapseRail
              side="left"
              collapsed={leftCollapsed}
              onToggle={toggleLeft}
              label="conversation list"
            />
            <CollapseRail
              side="right"
              collapsed={rightCollapsed}
              onToggle={toggleRight}
              label="contact panel"
            />
          </>
        )}
      </Box>

      <Box style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        {activeId ? (
          activeConvo?.contactId != null ? (
            <CrmPanel contactId={activeConvo.contactId} hideWhatsAppOnly />
          ) : (
            <CrmPanelEmpty />
          )
        ) : null}
      </Box>
    </Box>
  );
}

const TYPING_THROTTLE_MS = 8000;

function Conversation({ conversationId, convo }: { conversationId: number; convo?: IgConvo }) {
  // Keyset infinite query — newest page first, older pages lazy-loaded on
  // scroll-up by the thread's IntersectionObserver sentinel.
  const messages = trpc.instagram.inbox.messages.useInfiniteQuery(
    { conversationId, limit: THREAD_PAGE_LIMIT },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const sendText = trpc.instagram.inbox.sendText.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendMedia = trpc.instagram.inbox.sendMediaUpload.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendMediaFromLibrary = trpc.instagram.inbox.sendMediaFromLibrary.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [libraryOpen, setLibraryOpen] = useState(false);
  const sendReaction = trpc.instagram.inbox.sendReaction.useMutation({
    onSuccess: () => notifications.show({ message: 'Reaction sent', color: 'green' }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendQuickReplies = trpc.instagram.inbox.sendQuickReplies.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendGenericTemplate = trpc.instagram.inbox.sendGenericTemplate.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendButtonTemplate = trpc.instagram.inbox.sendButtonTemplate.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendMediaTemplate = trpc.instagram.inbox.sendMediaTemplate.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendMediaShare = trpc.instagram.inbox.sendMediaShare.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendProductTemplate = trpc.instagram.inbox.sendProductTemplate.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const sendTyping = trpc.instagram.inbox.sendTypingIndicator.useMutation();
  const utils = trpc.useUtils();
  const markAsRead = trpc.instagram.inbox.markAsRead.useMutation({
    // Clear the sidebar unread badge + nav unread total the instant a thread is
    // opened; without this onSuccess they only refresh on the 30s poll / reload.
    onSuccess: () => {
      void utils.instagram.inbox.conversations.invalidate();
      void utils.instagram.inbox.totalUnread.invalidate();
    },
  });
  const refreshProfile = trpc.instagram.inbox.refreshProfile.useMutation({
    onSuccess: (res) => {
      void utils.instagram.inbox.conversations.invalidate();
      if (res.ok === false) {
        notifications.show({
          message: 'No public profile is available for this user yet.',
          color: 'yellow',
        });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [draft, setDraft] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const lastTypingRef = useRef(0);
  const fileResetRef = useRef<() => void>(() => {});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (convo && convo.unreadCount > 0) markAsRead.mutate({ conversationId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, convo?.unreadCount]);

  const rows = useMemo(() => flattenThread(messages.data), [messages.data]);
  // Infinite-thread scroll: auto-load older on scroll-up, preserve read
  // position on prepend, follow the newest message when at the bottom.
  const { viewportRef, topSentinelRef, onScroll } = useThreadScroll({
    messageCount: rows.length,
    newestId: rows.length ? (rows[rows.length - 1]?.id ?? null) : null,
    oldestId: rows.length ? (rows[0]?.id ?? null) : null,
    hasMore: messages.hasNextPage,
    isFetchingOlder: messages.isFetchingNextPage,
    fetchOlder: messages.fetchNextPage,
    conversationKey: conversationId,
  });

  if (messages.isPending) return <Skeleton height={400} m="md" />;

  const grouped = groupMessages(rows.map((m) => ({ ...m, isIncoming: m.isIncomingMessage === 1 })));
  const windowState = convo
    ? messagingWindowState(convo.lastIncomingAt ? new Date(convo.lastIncomingAt) : null)
    : null;
  const windowClosed = windowState?.kind === 'closed';
  const contactUid = convo?.contactUid ?? null;

  function send() {
    if (!convo || !draft.trim()) return;
    sendText.mutate({
      instagramAccountId: convo.instagramAccountId,
      igsid: convo.igsid,
      text: draft.trim(),
    });
    setDraft('');
  }

  function onDraftChange(value: string) {
    setDraft(value);
    const now = Date.now();
    if (value.trim() && now - lastTypingRef.current > TYPING_THROTTLE_MS) {
      lastTypingRef.current = now;
      sendTyping.mutate({ conversationId });
    }
  }

  function onPickFile(file: File | null) {
    fileResetRef.current();
    if (!file || !convo) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      if (!base64) return;
      sendMedia.mutate({
        instagramAccountId: convo.instagramAccountId,
        igsid: convo.igsid,
        bytesBase64: base64,
        mimeType: file.type || 'application/octet-stream',
        filename: file.name,
      });
    };
    reader.readAsDataURL(file);
  }

  function reactTo(messageId: string, reaction: string) {
    if (!convo) return;
    sendReaction.mutate({
      instagramAccountId: convo.instagramAccountId,
      igsid: convo.igsid,
      messageId,
      reaction,
    });
  }

  /** Fire a saved template straight from the picker — dispatch by kind. */
  async function sendSavedTemplate(tpl: { kind: string; payload: unknown }) {
    if (!convo) return;
    const base = { instagramAccountId: convo.instagramAccountId, igsid: convo.igsid };
    const p = tpl.payload as Record<string, unknown>;
    try {
      switch (tpl.kind) {
        case 'quick_replies':
          await sendQuickReplies.mutateAsync({
            ...base,
            ...(p as { text: string; quickReplies: QuickReply[] }),
          });
          break;
        case 'generic':
          await sendGenericTemplate.mutateAsync({
            ...base,
            elements: (p as { elements: IgGenericCard[] }).elements,
          });
          break;
        case 'button':
          await sendButtonTemplate.mutateAsync({
            ...base,
            ...(p as { text: string; buttons: NonNullable<IgGenericCard['buttons']> }),
          });
          break;
        case 'media':
          await sendMediaTemplate.mutateAsync({
            ...base,
            ...(p as { mediaType: 'image' | 'video'; url: string; caption?: string }),
          });
          break;
        case 'media_share':
          await sendMediaShare.mutateAsync({ ...base, ...(p as { mediaId: string }) });
          break;
        case 'product':
          await sendProductTemplate.mutateAsync({ ...base, ...(p as { productIds: string[] }) });
          break;
      }
    } catch {
      /* mutation onError surfaces the toast */
    }
  }

  return (
    <Card p={0} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Group p="sm" justify="space-between" wrap="nowrap">
        <Group wrap="nowrap" style={{ minWidth: 0 }}>
          <Avatar size="md" radius="xl" src={convo ? igAvatarSrc(convo) : undefined}>
            <IconBrandInstagram size={14} />
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text fw={600} truncate>
              {convo ? igDisplayName(convo) : ''}
            </Text>
            <Text c="dimmed" size="xs" ff="monospace" truncate>
              {convo?.igsid}
            </Text>
          </div>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {windowState && (
            <Tooltip
              label={
                windowState.kind === 'human_agent'
                  ? 'Replies in this window are sent with the HUMAN_AGENT tag (up to 7 days).'
                  : windowState.kind === 'closed'
                    ? 'The customer must message you again before you can reply.'
                    : 'You can reply freely within 24 hours of the last inbound message.'
              }
            >
              <Badge color={windowState.color} variant="light" size="sm">
                {windowState.label}
              </Badge>
            </Tooltip>
          )}
          {contactUid && (
            <Tooltip label="Open contact in CRM">
              <ActionIcon
                variant="subtle"
                color="gray"
                renderRoot={(props) => (
                  <Link to="/vendor/contacts/$uid" params={{ uid: contactUid }} {...props} />
                )}
              >
                <IconUser size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Refresh profile">
            <ActionIcon
              variant="subtle"
              color="gray"
              loading={refreshProfile.isPending}
              onClick={() => refreshProfile.mutate({ conversationId })}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Divider />
      <ScrollArea
        style={{ flex: 1, padding: 12 }}
        viewportRef={viewportRef}
        onScrollPositionChange={onScroll}
      >
        {/* Top sentinel — scrolling it into view lazy-loads the next older page. */}
        <div ref={topSentinelRef} />
        {messages.isFetchingNextPage && (
          <Center mb="xs">
            <Loader size="xs" />
          </Center>
        )}
        <Stack gap={4}>
          {grouped.map((g) => (
            <Box key={g.label}>
              <DateDivider label={g.label} />
              {g.bursts.map((burst, bi) => (
                <Stack key={bi} gap={2} mb={6}>
                  {burst.messages.map((m, mi) => {
                    const isLast = mi === burst.messages.length - 1;
                    return (
                      <Box
                        key={m.id}
                        style={{
                          display: 'flex',
                          justifyContent: burst.isIncoming ? 'flex-start' : 'flex-end',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Paper
                          p="xs"
                          radius="md"
                          style={{
                            maxWidth: '72%',
                            // Theme-aware like the WhatsApp panel: `light-dark()`
                            // resolves against Mantine's color-scheme so text
                            // stays readable in both light and dark.
                            background: burst.isIncoming
                              ? 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))'
                              : 'var(--mantine-color-blue-6)',
                            color: burst.isIncoming
                              ? 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))'
                              : 'white',
                            border: burst.isIncoming
                              ? '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))'
                              : 'none',
                          }}
                        >
                          <IgMessageContent m={m} incoming={burst.isIncoming} />
                          {isLast && (
                            <Group justify="flex-end" gap={4} mt={2} wrap="nowrap">
                              <Text size="xs" c="dimmed">
                                {m.messagedAt ? formatTime(new Date(m.messagedAt)) : ''}
                              </Text>
                              {!burst.isIncoming && <ReadReceipt status={m.status} />}
                            </Group>
                          )}
                        </Paper>
                        {burst.isIncoming && m.externalMessageId && (
                          <ReactionMenu
                            disabled={sendReaction.isPending}
                            onPick={(r) => reactTo(m.externalMessageId as string, r)}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ))}
            </Box>
          ))}
          {rows.length === 0 && (
            <Text c="dimmed" ta="center" py="xl">
              No messages yet.
            </Text>
          )}
        </Stack>
      </ScrollArea>
      <Divider />
      {windowClosed && (
        <Text size="xs" c="dimmed" px="sm" pt="xs">
          The messaging window has closed — the customer must message you again before you can
          reply.
        </Text>
      )}
      <Group p="sm" wrap="nowrap" gap="xs" align="flex-end">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/png,image/jpeg,image/gif,video/mp4,audio/mpeg,audio/mp4,audio/aac,application/pdf"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            onPickFile(f);
            e.target.value = '';
          }}
        />
        <Menu position="top-start" withinPortal shadow="md">
          <Menu.Target>
            <Tooltip label="Attach">
              <ActionIcon
                size="lg"
                variant="subtle"
                color="gray"
                disabled={!convo || windowClosed}
                loading={sendMedia.isPending}
              >
                <IconPaperclip size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconUpload size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload from device
            </Menu.Item>
            <Menu.Item leftSection={<IconPhoto size={16} />} onClick={() => setLibraryOpen(true)}>
              Choose from library
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <QuickRepliesPopover
          channel="instagram"
          onPick={(body) => setDraft((d) => (d ? `${d} ${body}` : body))}
        />
        <TemplatePickerMenu
          disabled={!convo || windowClosed}
          onPick={(tpl) => void sendSavedTemplate(tpl)}
        />
        <Tooltip label="Emoji">
          <ActionIcon
            size="lg"
            variant="subtle"
            color="gray"
            disabled={!convo}
            onClick={() => setEmojiOpen(true)}
          >
            <IconMoodSmile size={18} />
          </ActionIcon>
        </Tooltip>
        <Textarea
          flex={1}
          autosize
          minRows={1}
          maxRows={6}
          placeholder="Type an Instagram reply…"
          value={draft}
          onChange={(e) => onDraftChange(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Tooltip label="Send (Enter)">
          <ActionIcon
            size="lg"
            variant="filled"
            color="blue"
            disabled={!draft.trim() || !convo || windowClosed}
            loading={sendText.isPending}
            onClick={send}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <MediaLibraryPicker
        opened={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        busy={sendMediaFromLibrary.isPending}
        onSelect={(item) => {
          if (convo) {
            sendMediaFromLibrary.mutate({
              instagramAccountId: convo.instagramAccountId,
              igsid: convo.igsid,
              mediaAssetId: item.id,
            });
          }
          setLibraryOpen(false);
        }}
      />

      <Modal
        opened={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        withCloseButton={false}
        padding={0}
        size="auto"
        centered
        zIndex={1000}
      >
        <Suspense fallback={<Loader m="xl" />}>
          <EmojiPicker
            onEmojiClick={(e) => {
              setDraft((d) => d + e.emoji);
              setEmojiOpen(false);
            }}
          />
        </Suspense>
      </Modal>
    </Card>
  );
}

// ── Rich-composer types + builders ──

export interface QuickReply {
  title: string;
  payload: string;
}

export interface CarouselButton {
  type: 'web_url' | 'postback';
  title: string;
  url?: string;
  payload?: string;
}

export interface CarouselCard {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  buttons?: CarouselButton[];
}

/** API-shaped card (strict discriminated button union) — what the send/save
 *  endpoints accept. The builder edits the looser `CarouselCard` and normalizes
 *  to this on submit. */
export interface IgGenericCard {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  buttons?: Array<
    | { type: 'web_url'; title: string; url: string }
    | { type: 'postback'; title: string; payload: string }
  >;
}

/** Normalize an editor card into the strict API shape (drops the unused
 *  url/payload field per the button's type, trims empty optionals). */
function toApiCard(c: CarouselCard): IgGenericCard {
  return {
    title: c.title.trim(),
    ...(c.subtitle?.trim() ? { subtitle: c.subtitle.trim() } : {}),
    ...(c.imageUrl?.trim() ? { imageUrl: c.imageUrl.trim() } : {}),
    ...(c.buttons && c.buttons.length > 0
      ? {
          buttons: c.buttons.map((b) =>
            b.type === 'web_url'
              ? { type: 'web_url' as const, title: b.title, url: b.url ?? '' }
              : { type: 'postback' as const, title: b.title, payload: b.payload ?? '' },
          ),
        }
      : {}),
  };
}

/** Builder for a text prompt + up to 13 tappable quick-reply chips. */
export function QuickReplyBuilder({
  onSend,
  onChange,
  sending,
  submitLabel = 'Send',
  initial,
}: {
  onSend: (p: { text: string; quickReplies: QuickReply[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { text: string; quickReplies: QuickReply[] };
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [replies, setReplies] = useState<QuickReply[]>(
    initial?.quickReplies?.length ? initial.quickReplies : [{ title: '', payload: '' }],
  );
  useEffect(() => {
    onChange?.({ text, quickReplies: replies });
  }, [text, replies, onChange]);

  const valid =
    text.trim().length > 0 &&
    replies.length > 0 &&
    replies.every((r) => r.title.trim() && r.payload.trim());

  function update(i: number, patch: Partial<QuickReply>) {
    setReplies((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <Stack gap="sm">
      <Textarea
        label="Message"
        placeholder="Ask a question…"
        autosize
        minRows={2}
        maxRows={4}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <Text size="sm" fw={600}>
        Options ({replies.length}/13)
      </Text>
      {replies.map((r, i) => (
        <Group key={i} wrap="nowrap" gap="xs" align="flex-end">
          <TextInput
            flex={1}
            label={i === 0 ? 'Button label' : undefined}
            placeholder="Yes, tell me more"
            maxLength={20}
            value={r.title}
            onChange={(e) => update(i, { title: e.currentTarget.value })}
          />
          <TextInput
            flex={1}
            label={i === 0 ? 'Payload (tracked on tap)' : undefined}
            placeholder="INTERESTED"
            value={r.payload}
            onChange={(e) => update(i, { payload: e.currentTarget.value })}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            disabled={replies.length === 1}
            onClick={() => setReplies((rs) => rs.filter((_, idx) => idx !== i))}
            aria-label="Remove option"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Group justify="space-between">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={replies.length >= 13}
          onClick={() => setReplies((rs) => [...rs, { title: '', payload: '' }])}
        >
          Add option
        </Button>
        <Button
          color="blue"
          loading={sending}
          disabled={!valid}
          onClick={() =>
            onSend({
              text: text.trim(),
              quickReplies: replies.map((r) => ({
                title: r.title.trim(),
                payload: r.payload.trim(),
              })),
            })
          }
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Builder for a generic/carousel template — up to 10 cards, ≤3 buttons each. */
export function CarouselBuilder({
  onSend,
  onChange,
  sending,
  submitLabel = 'Send',
  initial,
}: {
  onSend: (cards: IgGenericCard[]) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: IgGenericCard[];
}) {
  const [cards, setCards] = useState<CarouselCard[]>(
    initial?.length
      ? initial.map((c) => ({
          title: c.title,
          subtitle: c.subtitle,
          imageUrl: c.imageUrl,
          buttons: (c.buttons ?? []).map((b) =>
            b.type === 'web_url'
              ? { type: 'web_url' as const, title: b.title, url: b.url }
              : { type: 'postback' as const, title: b.title, payload: b.payload },
          ),
        }))
      : [{ title: '', buttons: [] }],
  );
  useEffect(() => {
    onChange?.({
      elements: cards.map((c) => ({
        title: c.title,
        subtitle: c.subtitle,
        imageUrl: c.imageUrl,
        buttons: (c.buttons ?? []).map((b) => ({ type: b.type, title: b.title, url: b.url })),
      })),
    });
  }, [cards, onChange]);

  const valid = cards.length > 0 && cards.every((c) => c.title.trim());

  function updateCard(i: number, patch: Partial<CarouselCard>) {
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  return (
    <Stack gap="md">
      {cards.map((card, i) => (
        <Card key={i} withBorder padding="sm">
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600}>
              Card {i + 1}
            </Text>
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={cards.length === 1}
              onClick={() => setCards((cs) => cs.filter((_, idx) => idx !== i))}
              aria-label="Remove card"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <Stack gap="xs">
            <TextInput
              label="Title"
              placeholder="Summer Collection"
              maxLength={80}
              value={card.title}
              onChange={(e) => updateCard(i, { title: e.currentTarget.value })}
            />
            <TextInput
              label="Subtitle"
              placeholder="Up to 40% off"
              maxLength={80}
              value={card.subtitle ?? ''}
              onChange={(e) => updateCard(i, { subtitle: e.currentTarget.value })}
            />
            <TextInput
              label="Image URL"
              placeholder="https://…/product.jpg"
              value={card.imageUrl ?? ''}
              onChange={(e) => updateCard(i, { imageUrl: e.currentTarget.value })}
            />
            <CardButtonsEditor
              buttons={card.buttons ?? []}
              onChange={(buttons) => updateCard(i, { buttons })}
            />
          </Stack>
        </Card>
      ))}
      <Group justify="space-between">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={cards.length >= 10}
          onClick={() => setCards((cs) => [...cs, { title: '', buttons: [] }])}
        >
          Add card
        </Button>
        <Button
          color="blue"
          loading={sending}
          disabled={!valid}
          onClick={() => onSend(cards.map(toApiCard))}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Inline repeater for a card's buttons (≤3, web link or postback). */
function CardButtonsEditor({
  buttons,
  onChange,
}: {
  buttons: CarouselButton[];
  onChange: (b: CarouselButton[]) => void;
}) {
  return (
    <Stack gap="xs">
      {buttons.map((b, i) => (
        <Group key={i} wrap="nowrap" gap="xs" align="flex-end">
          <Select
            w={120}
            data={[
              { value: 'web_url', label: 'Link' },
              { value: 'postback', label: 'Postback' },
            ]}
            value={b.type}
            allowDeselect={false}
            onChange={(v) =>
              onChange(
                buttons.map((x, idx) =>
                  idx === i ? { ...x, type: (v as 'web_url' | 'postback') ?? 'web_url' } : x,
                ),
              )
            }
          />
          <TextInput
            flex={1}
            placeholder="Button text"
            maxLength={20}
            value={b.title}
            onChange={(e) =>
              onChange(
                buttons.map((x, idx) => (idx === i ? { ...x, title: e.currentTarget.value } : x)),
              )
            }
          />
          <TextInput
            flex={1}
            placeholder={b.type === 'web_url' ? 'https://…' : 'PAYLOAD'}
            value={b.type === 'web_url' ? (b.url ?? '') : (b.payload ?? '')}
            onChange={(e) =>
              onChange(
                buttons.map((x, idx) =>
                  idx === i
                    ? b.type === 'web_url'
                      ? { ...x, url: e.currentTarget.value }
                      : { ...x, payload: e.currentTarget.value }
                    : x,
                ),
              )
            }
          />
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => onChange(buttons.filter((_, idx) => idx !== i))}
            aria-label="Remove button"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      {buttons.length < 3 && (
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconPlus size={12} />}
          onClick={() => onChange([...buttons, { type: 'web_url', title: '', url: '' }])}
        >
          Add button
        </Button>
      )}
    </Stack>
  );
}

/** Menu listing the vendor's saved DM templates; sends the chosen one. */
function TemplatePickerMenu({
  onPick,
  disabled,
}: {
  onPick: (tpl: { kind: string; payload: unknown }) => void;
  disabled?: boolean;
}) {
  const templates = trpc.instagram.templates.list.useQuery({});
  const rows = templates.data ?? [];
  return (
    <Menu position="top" withinPortal shadow="sm" width={240}>
      <Menu.Target>
        <Tooltip label="Saved templates">
          <ActionIcon size="lg" variant="subtle" color="gray" disabled={disabled}>
            <IconTemplate size={18} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Saved templates</Menu.Label>
        {rows.length === 0 && (
          <Menu.Item disabled>{templates.isPending ? 'Loading…' : 'None saved yet'}</Menu.Item>
        )}
        {rows.map((t) => (
          <Menu.Item
            key={t.uid}
            leftSection={
              t.kind === 'generic' ? <IconLayoutGrid size={14} /> : <IconBolt size={14} />
            }
            onClick={() => onPick({ kind: t.kind, payload: t.payload })}
          >
            {t.name}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item
          component={Link}
          to="/vendor/instagram/templates"
          leftSection={<IconTemplate size={14} />}
        >
          Manage templates…
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function ReactionMenu({
  onPick,
  disabled,
}: {
  onPick: (reaction: string) => void;
  disabled?: boolean;
}) {
  return (
    <Menu position="right" withinPortal shadow="sm">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="sm" disabled={disabled} aria-label="React">
          <IconMoodSmile size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Group gap={2} p={4} wrap="nowrap">
          {IG_REACTIONS.map((r) => (
            <ActionIcon
              key={r.reaction}
              variant="subtle"
              size="lg"
              onClick={() => onPick(r.reaction)}
              aria-label={r.reaction}
            >
              <span style={{ fontSize: 18 }}>{r.emoji}</span>
            </ActionIcon>
          ))}
        </Group>
      </Menu.Dropdown>
    </Menu>
  );
}

export const Route = createFileRoute('/vendor/instagram/inbox')({
  component: InstagramInbox,
  validateSearch: (s: Record<string, unknown>): { contactId?: number } => {
    const n = typeof s.contactId === 'number' ? s.contactId : Number(s.contactId);
    return Number.isFinite(n) && n > 0 ? { contactId: n } : {};
  },
});
